import {
  GetServerSideProps,
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  PreviewData,
} from "next";
import { ParsedUrlQuery } from "querystring";

import {
  getQueryForGeoLocationData,
  getQueryParamsForCollegeListing,
  fetchDualCategory,
  generateCollegeListingFilters,
  parseCollegesParam,
  fetchListingPageDataWithPagination,
  searchLocationWithData,
  generateSpecialWithCourseClgListFilters,
  getCourseSpeInfo,
  getFeaturedCollegeQueryParams,
  getBannerQueryParams,
  getArticlesQueryParams,
  filterInfoDataQueryParams,
  filterSectionStreamQueryParams,
} from "@/utils/collegeListingPageQuery";

import { SEOHead } from "@/src/Components/seo";
import CollegeListItem from "@/src/Components/collegeListingPage/collegeListItem";
import { RootLayouts } from "@/src/Layouts";
import { generateClgListSEOData } from "@/src/utils/seo-utils/seoClgListUtils";

import dynamic from "next/dynamic";
import { parseCollegesParamTwo } from "@/utils/collegeListingPageQuery/parseCollegesParamTwo";
import { buildGovtClgListFilters } from "@/utils/govtCollegesListingPage/buildGovtClgListFilters.ts";
// import { fetchFooterData } from "@/utils/commanQuery/fetchFooterData";
import { getFilterParams } from "@/utils/helpers/filterParams";
import { getBannerFilterParams } from "@/utils/helpers/filterBannerParams";
import Banner from "@/src/Components/banner/BannerComponent";
import React from "react";
import ArticleComponent from "@/src/Components/common/ArticleComponent";
import ListingPageFilterComponent from "@/src/CollegeFilterListingPage";
import { Breadcrumb } from "@/src/Components/ui/common/breadCrumbs/BreadCrumbs";
import Head from "next/head";

const CollegeListSection = dynamic(
  () => import("@/src/Components/collegeListingPage/collegeListSection"),
  {
    ssr: false,
  }
);

// Function to format college parameter by removing hyphens and capitalizing words
const formatCollegeParam = (param: string): string => {
  if (!param) return "";

  // Split by hyphens and capitalize each word
  return param
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function CollegePage({
  colleges,
  collegesParam,
  slugInfo,
  filters,
  featuredColleges,
  bannerData,
  articleData,
  geoLocation,
  filterData,
  filterSecStreamData,
}: any) {
  const seoData = generateClgListSEOData(
    slugInfo,
    collegesParam,
    articleData?.seo
  );

  const { page_title_content } = seoData;

  const featuredCollegeIds = new Set(
    featuredColleges?.map((college: any) => college.id)
  );

  const filteredColleges = colleges?.results?.filter(
    (college: any) => !featuredCollegeIds?.has(college?.id)
  );

  const finalCollegeList = [...featuredColleges, ...filteredColleges];

  const domainUrl = process.env.NEXT_PUBLIC_DOMAIN_URL;

  const breadcrumbItems = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item:
        slugInfo?.country_name === "India"
          ? `${domainUrl}`
          : `${domainUrl}/study-abroad`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: `${formatCollegeParam(collegesParam)}`,
      item: `${domainUrl}/${collegesParam}`,
    },
  ];

  // Create ItemList schema for colleges
  const collegeListSchema = {
    "@context": "http://schema.org",
    "@type": "ItemList",
    name: articleData?.heading ?? page_title_content,
    description:
      articleData?.description ??
      `Check here the list of best ${
        articleData?.heading ?? page_title_content
      } along with their fees structure, admission process, courses, reviews, placements, ranking and eligibility criteria details at CollegeHai.`,
    numberOfItems: colleges?.pagination?.total || 0,
    url: `${domainUrl}/${collegesParam}`,
    itemListElement: finalCollegeList
      ?.slice(0, 30)
      .map((college: any, index: number) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${domainUrl}/colleges/${college?.college_url}/college-info`,
      })),
  };
  
  return (
    <>
      <SEOHead seoData={seoData} />
      <RootLayouts>
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: breadcrumbItems,
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(collegeListSchema),
            }}
          />
        </Head>
        <section className="bg-gray-50">
          <section className="mt-20 lg:pt-8">
            <div className="w-[95%] md:w-[90%] mx-auto">
              <Breadcrumb color="text-primary" items={breadcrumbItems} />
            </div>
            <h1 className="w-[95%] md:w-[90%] mx-auto text-xl md:text-2xl font-bold leading-tight text-primary">
              {articleData?.heading ?? page_title_content}
            </h1>

            {/* {page_content_description ? (
            <p className="w-[95%] md:w-[90%] mx-auto text-sm md:text-base font-normal leading-relaxed text-gray-800 mt-2">
              {formatDescriptionToJSX(page_content_description)}
            </p>
          ) : null} */}
            {/* Article Overview component */}
            {articleData && articleData?.page_data?.length > 0 && (
              <ArticleComponent articleData={articleData} />
            )}
            {bannerData?.results && <Banner banners={bannerData?.results} />}
          </section>
          {/* Filter component */}
          <section>
            <div className="lg:flex flex-col relative gap-y-4 lg:pb-2 lg:pt-2 font-inter">
             <div className="flex items-center bg-white sticky lg:top-[85px] top-14 !z-50 lg:w-[90%] lg:mx-auto md:mx-10 py-2 lg:py-3 border px-4 rounded shadow-lg ">

                <ListingPageFilterComponent
                  geoLocation={geoLocation}
                  filterData={filterData}
                  filterSecStreamData={filterSecStreamData}
                />
              </div>

              {/* COllege List section */}
              <div className="flex-1 w-full z-10 md:mt-2">
                <div className="flex-1 w-[95%] md:w-[90%] mx-auto">
                  <div className="flex gap-2 justify-between items-center lg:items-start lg:mt-[5px] md:mt-9 my-2">
                    <div className="px-2 text-primary-text text-base">
                      Showing{" "}
                      <span className="font-semibold">
                        {colleges?.pagination?.total}{" "}
                      </span>{" "}
                      Colleges
                    </div>
                    {/* <div className="relative ">
                    <div className="flex border-2 items-center px-4 py-1 border-extra-light-text gap-2 rounded-lg cursor-pointer z-5">
                      <span className="text-sm hidden md:block">Sort</span>
                      <SortIcon />
                    </div>
                  </div> */}
                  </div>

                  <div className="w-full">
                    {finalCollegeList?.map((college: any, index: number) => {
                      const isIndianCollege = collegesParam.includes("-india");
                      const isFeatured = featuredCollegeIds?.has(college?.id);

                      return (
                        <div className="relative w-full" key={college?.id}>
                          <CollegeListItem
                            college={college}
                            id={college?.college_url}
                            className="w-full"
                            isAbroadCollege={!isIndianCollege}
                          />
                          {isFeatured && (
                            <div className="featuredTag">
                              <span className="text-white text-[0.7rem]">
                                Featured
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <CollegeListSection
                    collegesParam={collegesParam}
                    filters={filters}
                    featuredColleges={featuredCollegeIds}
                  />
                </div>
              </div>
            </div>
          </section>
        </section>
      </RootLayouts>
    </>
  );
}

// set operator for "equal to" for countrries and "not equal to" for abroad
const determineOperatorAndGeoLocation = (locationInfo: string) => {
  if (locationInfo === "abroad") {
    return { operator: "$nei", country: "India" };
  } else {
    return { operator: "$eqi", country: locationInfo };
  }
};

// check if slug is really related to a location
const validateSlugInfo = (slugInfo: any, locationInfo: string) => {
  if (!slugInfo && locationInfo !== "abroad") {
    return false;
  }

  if (
    slugInfo?.type !== "city" &&
    slugInfo?.type !== "state" &&
    slugInfo?.type !== "country" &&
    locationInfo !== "abroad"
  ) {
    return false;
  }

  return true;
};

async function fetchBannerData(filters: any) {
  const { filter, countryValue } = filters;
  let banners: any = null;

  if (filter) {
    const primaryQueryParams = getBannerQueryParams(filter);
    banners = await fetchListingPageDataWithPagination(
      JSON.stringify(primaryQueryParams),
      primaryQueryParams,
      "banners"
    );
  }

  if (!filter || banners?.results?.length === 0) {
    const defaultFilter = countryValue?.toLowerCase().includes("india")
      ? { is_default_banner_india: true }
      : { is_default_banner_abroad: true };

    const defaultQueryParams = getBannerQueryParams(defaultFilter);
    banners = await fetchListingPageDataWithPagination(
      JSON.stringify(defaultQueryParams),
      defaultQueryParams,
      "banners"
    );
  }

  return banners;
}

/** Handle single segment in the route, e.g. `/btech-colleges-in-india` */
async function handleSingleParam(
  collegesParam: string
): Promise<GetServerSidePropsResult<any>> {
  const { type, college_category, locationInfo, urlAcademicCategory } =
    parseCollegesParam(collegesParam);

  const articleQueryParams = getArticlesQueryParams(collegesParam);
  if (!locationInfo) {
    return { notFound: true };
  }

  if (
    college_category &&
    ["iit", "nit", "government"].includes(college_category)
  ) {
    try {
      const page = null;
      const pageSize = 25;
      const queryParamsWithoutFilter = getQueryParamsForCollegeListing(
        page,
        pageSize
      );

      const filters = buildGovtClgListFilters(
        locationInfo,
        urlAcademicCategory,
        college_category as "government" | "iit" | "nit"
      );

      // Merge filters with existing query params
      const queryParams = { ...queryParamsWithoutFilter, filters };

      const colleges = await fetchListingPageDataWithPagination(
        JSON.stringify(queryParams),
        queryParams,
        "colleges"
      );
      const defaultQueryParams = getBannerQueryParams();
      const banners = await fetchListingPageDataWithPagination(
        JSON.stringify(defaultQueryParams),
        defaultQueryParams,
        "banners"
      );

      const articleData = await fetchListingPageDataWithPagination(
        JSON.stringify(articleQueryParams),
        articleQueryParams,
        "articles"
      );

      // If the API call returns no results, serve a 404
      if (
        !colleges?.results ||
        !Array.isArray(colleges.results) ||
        colleges.results.length === 0
      ) {
        console.warn("No colleges found for this location. Returning 404.");
        return { notFound: true };
      }

      // get footer data
      // const footerData = await fetchFooterData();

      const filterSectionLocationData =
        await fetchListingPageDataWithPagination(
          JSON.stringify(getQueryForGeoLocationData),
          getQueryForGeoLocationData,
          "countries"
        );
      const filterSectionGeoLocation = filterSectionLocationData?.results ?? [];

      const filterSectionDataRes = await fetchListingPageDataWithPagination(
        JSON.stringify(filterInfoDataQueryParams),
        filterInfoDataQueryParams,
        "colleges"
      );

      const filterSectionData = filterSectionDataRes?.results ?? [];

      const filterSectionStreamDataRes =
        await fetchListingPageDataWithPagination(
          JSON.stringify(filterSectionStreamQueryParams),
          filterSectionStreamQueryParams,
          "streams"
        );

      const filterSectionStreamData = filterSectionStreamDataRes?.results ?? [];

      // Return data as props

      // const end = Date.now(); // End time
      // const ssrTime = end - startTime; // Calculate total SSR time

      return {
        props: {
          colleges,
          collegesParam,
          filters,
          // footerData,
          featuredColleges: [],
          articleData: articleData?.results?.[0] ?? [],
          bannerData: banners ?? [],
          geoLocation: filterSectionGeoLocation,
          filterData: filterSectionData,
          filterSecStreamData: filterSectionStreamData,
        },
      };
    } catch (error) {
      console.error("Error fetching colleges:", error);
      return { notFound: true };
    }
  }

  // 1) Geo location
  const { operator, country } = determineOperatorAndGeoLocation(locationInfo);

  const response = await fetchListingPageDataWithPagination(
    JSON.stringify(getQueryForGeoLocationData),
    getQueryForGeoLocationData,
    "countries"
  );

  const locationData = response?.results ?? [];
  if (!locationData.length) {
    return { notFound: true };
  }

  const slugInfo: any = searchLocationWithData(locationInfo, locationData);
  if (!validateSlugInfo(slugInfo, locationInfo)) {
    return { notFound: true };
  }

  // 2) Check academic category
  let dualCategory = null;
  if (type === "dual-category" && urlAcademicCategory) {
    dualCategory = await fetchDualCategory(type, urlAcademicCategory);
    if (!dualCategory?.categoryFilterValue) {
      return { notFound: true };
    }
  }

  // 3) Build filters + fetch listing
  try {
    const dynamicPage = null;
    const dynamicPageSize = 25;
    const queryParamsWithoutFilter = getQueryParamsForCollegeListing(
      dynamicPage,
      dynamicPageSize
    );
    const filters = generateCollegeListingFilters(
      slugInfo,
      operator,
      country,
      dualCategory
    );

    const queryParams = { ...queryParamsWithoutFilter, filters };

    const filterValue = getFilterParams(
      dualCategory ? { ...slugInfo, ...dualCategory } : slugInfo
    );

    const bannerFilterValue = getBannerFilterParams(
      dualCategory ? { ...slugInfo, ...dualCategory } : slugInfo
    );

    const featuredQueryParams = getFeaturedCollegeQueryParams(filterValue);

    const banners = await fetchBannerData(bannerFilterValue);

    const articleData = await fetchListingPageDataWithPagination(
      JSON.stringify(articleQueryParams),
      articleQueryParams,
      "articles"
    );

    const colleges = await fetchListingPageDataWithPagination(
      JSON.stringify(queryParams),
      queryParams,
      "colleges"
    );

    const featuredColleges = await fetchListingPageDataWithPagination(
      JSON.stringify(featuredQueryParams),
      featuredQueryParams,
      "colleges"
    );

    if (!Array.isArray(colleges?.results) || colleges?.results?.length === 0) {
      return { notFound: true };
    }

    // get footer data
    // const footerData = await fetchFooterData();

    const filterSectionLocationData = await fetchListingPageDataWithPagination(
      JSON.stringify(getQueryForGeoLocationData),
      getQueryForGeoLocationData,
      "countries"
    );
    const filterSectionGeoLocation = filterSectionLocationData?.results ?? [];

    const filterSectionDataRes = await fetchListingPageDataWithPagination(
      JSON.stringify(filterInfoDataQueryParams),
      filterInfoDataQueryParams,
      "colleges"
    );

    const filterSectionData = filterSectionDataRes?.results ?? [];

    const filterSectionStreamDataRes = await fetchListingPageDataWithPagination(
      JSON.stringify(filterSectionStreamQueryParams),
      filterSectionStreamQueryParams,
      "streams"
    );

    const filterSectionStreamData = filterSectionStreamDataRes?.results ?? [];

    // const end = Date.now(); // End time
    // const ssrTime = end - startTime; // Calculate total SSR time
    return {
      props: {
        colleges,
        collegesParam,
        slugInfo: dualCategory ? { ...slugInfo, ...dualCategory } : slugInfo,
        filters,
        // footerData,
        featuredColleges: featuredColleges?.results?.slice(0, 10) ?? [],
        bannerData: banners ?? [],
        articleData: articleData?.results?.[0] ?? [],
        geoLocation: filterSectionGeoLocation,
        filterData: filterSectionData,
        filterSecStreamData: filterSectionStreamData,
      },
    };
  } catch (error) {
    return { props: { error: "Failed to fetch listing data." } };
  }
}

/** Handle two segments, e.g. `/btech/computer-science-colleges-in-india` */
async function handleTwoParams([courseName, slugPart]: [
  string,
  string
]): Promise<GetServerSidePropsResult<any>> {
  // Just an example. You can expand logic as needed.

  const { specialization, geoLocation } = parseCollegesParamTwo(slugPart);

  const articleQueryParams = getArticlesQueryParams(
    `/${courseName}/${slugPart}`
  );

  if (!specialization || !geoLocation || !courseName) {
    return { notFound: true };
  }

  // 0) check Specialization and CourseName
  const courseWithSpeData = await getCourseSpeInfo(courseName, specialization);

  if (!courseWithSpeData) {
    return { notFound: true };
  }

  // 1) Geo location
  const { operator, country } = determineOperatorAndGeoLocation(geoLocation);

  const response = await fetchListingPageDataWithPagination(
    JSON.stringify(getQueryForGeoLocationData),
    getQueryForGeoLocationData,
    "countries"
  );

  const locationData = response?.results ?? [];
  if (!locationData.length) {
    return { notFound: true };
  }

  const slugInfo = searchLocationWithData(geoLocation, locationData);

  if (!validateSlugInfo(slugInfo, geoLocation)) {
    return { notFound: true };
  }

  const dynamicPage = null;
  const dynamicPageSize = 25;
  const queryParamsWithoutFilter = getQueryParamsForCollegeListing(
    dynamicPage,
    dynamicPageSize
  );

  const filters = generateSpecialWithCourseClgListFilters(
    slugInfo,
    operator,
    country,
    courseName,
    specialization
  );

  const queryParams = { ...queryParamsWithoutFilter, filters };

  const defaultQueryParams = getBannerQueryParams();
  const banners = await fetchListingPageDataWithPagination(
    JSON.stringify(defaultQueryParams),
    defaultQueryParams,
    "banners"
  );

  const colleges = await fetchListingPageDataWithPagination(
    JSON.stringify(queryParams),
    queryParams,
    "colleges"
  );

  const articleData = await fetchListingPageDataWithPagination(
    JSON.stringify(articleQueryParams),
    articleQueryParams,
    "articles"
  );

  if (!Array.isArray(colleges?.results) || colleges?.results?.length === 0) {
    return { notFound: true };
  }

  // get footer data
  // const footerData = await fetchFooterData();

  const filterSectionLocationData = await fetchListingPageDataWithPagination(
    JSON.stringify(getQueryForGeoLocationData),
    getQueryForGeoLocationData,
    "countries"
  );
  const filterSectionGeoLocation = filterSectionLocationData?.results ?? [];

  const filterSectionDataRes = await fetchListingPageDataWithPagination(
    JSON.stringify(filterInfoDataQueryParams),
    filterInfoDataQueryParams,
    "colleges"
  );

  const filterSectionData = filterSectionDataRes?.results ?? [];

  const filterSectionStreamDataRes = await fetchListingPageDataWithPagination(
    JSON.stringify(filterSectionStreamQueryParams),
    filterSectionStreamQueryParams,
    "streams"
  );

  const filterSectionStreamData = filterSectionStreamDataRes?.results ?? [];

  return {
    props: {
      colleges,
      collegesParam: `${courseName}/${slugPart}`,
      slugInfo: courseWithSpeData
        ? { ...slugInfo, ...courseWithSpeData }
        : slugInfo,
      filters,
      // footerData,
      featuredColleges: [],
      bannerData: banners ?? [],
      articleData: articleData?.results?.[0] ?? [],
      geoLocation: filterSectionGeoLocation,
      filterData: filterSectionData,
      filterSecStreamData: filterSectionStreamData,
    },
  };
}

export const getServerSideProps: GetServerSideProps<any> = async (
  context: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>
) => {
  try {
    const raw = context.params?.colleges;
    if (!raw) {
      return { notFound: true };
    }

    // Convert to array for easy handling
    const segments = Array.isArray(raw) ? raw : [raw];

    if (segments?.length === 0) {
      return { notFound: true };
    }

    // Decide what to do based on how many segments
    switch (segments?.length) {
      case 1:
        return handleSingleParam(segments[0]);
      case 2:
        return handleTwoParams([segments[0], segments[1]]);
      default:
        return { notFound: true };
    }
  } catch (error) {
    return { props: { error: "Failed to parse the URL." } };
  }
};
